import { useEffect, useRef } from 'react'
import { useNotifications } from '../contexts/NotificationContext'

export const useStockAlerts = (devices, deviceStatuses, userAlertThresholds = null) => {
  const { addNotification } = useNotifications()
  const previousAlertsRef = useRef(new Set())

  // Default alert thresholds if user doesn't have custom ones
  const defaultThresholds = {
    stock: {
      critical: 20,
      warning: 50,
      low: 70
    },
    enabled: {
      stockAlerts: true,
      deviceOffline: true,
      maintenance: true
    }
  }

  // Properly merge user's custom thresholds with defaults to ensure all properties exist
  const alertThresholds = {
    stock: {
      ...defaultThresholds.stock,
      ...(userAlertThresholds?.stock || {})
    },
    enabled: {
      ...defaultThresholds.enabled,
      ...(userAlertThresholds?.enabled || {})
    }
  }

  // Calculate stock level for a device
  const calculateStockLevel = (device) => {
    // Handle both slot-based data and product-based data
    let totalCapacity = 0
    let currentQuantity = 0

    // Check for slots data structure first
    if (device.slots && Object.keys(device.slots).length > 0) {
      Object.values(device.slots).forEach(slot => {
        const capacity = slot.capacity || slot.quantity || 0
        const quantity = slot.quantity || 0
        totalCapacity += capacity
        currentQuantity += quantity
      })
    } 
    // Check for product-based data structure (Firebase format)
    else {
      Object.keys(device).forEach(key => {
        if (key.startsWith('product_')) {
          const productData = device[key]
          if (productData && typeof productData === 'object') {
            // Use capacity as max stock, current quantity as current stock
            const capacity = productData.capacity || productData.max_quantity || 10 // Default capacity
            const quantity = productData.quantity || 0
            totalCapacity += capacity
            currentQuantity += quantity
          }
        }
      })
    }

    const percentage = totalCapacity > 0 ? Math.round((currentQuantity / totalCapacity) * 100) : 0
    return { percentage, totalCapacity, currentQuantity }
  }

  useEffect(() => {
    if (!devices || devices.length === 0) return

    const currentAlerts = new Set()
    
    devices.forEach(device => {
      const stockData = calculateStockLevel(device)
      const deviceStatus = deviceStatuses[device.id]
      const deviceName = device.name || device.id

      // Stock Level Alerts - use user-configurable thresholds
      if (stockData.totalCapacity > 0 && alertThresholds.enabled.stockAlerts) {
        const alertKey = `${device.id}_stock`
        
        if (stockData.percentage === 0) {
          // Out of stock - Critical
          const criticalKey = `${alertKey}_critical`
          currentAlerts.add(criticalKey)
          
          if (!previousAlertsRef.current.has(criticalKey)) {
            addNotification({
              title: 'Out of Stock Alert',
              message: `${deviceName} is completely out of stock`,
              priority: 'critical',
              category: 'stockAlerts',
              deviceId: device.id,
              deviceName,
              stockPercentage: 0
            })
          }
        } else if (stockData.percentage < alertThresholds.stock.critical) {
          // Critical low stock - user configurable threshold
          const criticalKey = `${alertKey}_critical`
          currentAlerts.add(criticalKey)
          
          if (!previousAlertsRef.current.has(criticalKey)) {
            addNotification({
              title: 'Critical Stock Alert',
              message: `${deviceName} is critically low on stock (${stockData.percentage}%)`,
              priority: 'critical',
              category: 'stockAlerts',
              deviceId: device.id,
              deviceName,
              stockPercentage: stockData.percentage
            })
          }
        } else if (stockData.percentage < alertThresholds.stock.warning) {
          // Warning stock - user configurable threshold
          const warningKey = `${alertKey}_warning`
          currentAlerts.add(warningKey)
          
          if (!previousAlertsRef.current.has(warningKey)) {
            addNotification({
              title: 'Low Stock Warning',
              message: `${deviceName} stock is below warning threshold (${stockData.percentage}%)`,
              priority: 'warning',
              category: 'stockAlerts',
              deviceId: device.id,
              deviceName,
              stockPercentage: stockData.percentage
            })
          }
        } else if (stockData.percentage < alertThresholds.stock.low) {
          // Low stock info - user configurable threshold
          const infoKey = `${alertKey}_info`
          currentAlerts.add(infoKey)
          
          if (!previousAlertsRef.current.has(infoKey)) {
            addNotification({
              title: 'Stock Level Notice',
              message: `${deviceName} stock is below low threshold (${stockData.percentage}%)`,
              priority: 'info',
              category: 'stockAlerts',
              deviceId: device.id,
              deviceName,
              stockPercentage: stockData.percentage
            })
          }
        }
      }

      // Device Status Alerts - check if enabled
      const statusKey = `${device.id}_status`
      
      if ((deviceStatus === 'down' || deviceStatus === 'offline') && alertThresholds.enabled.deviceOffline) {
        const offlineKey = `${statusKey}_offline`
        currentAlerts.add(offlineKey)
        
        if (!previousAlertsRef.current.has(offlineKey)) {
          addNotification({
            title: 'Device Offline',
            message: `${deviceName} has gone offline`,
            priority: 'warning',
            category: 'deviceOffline',
            deviceId: device.id,
            deviceName,
            status: deviceStatus
          })
        }
      } else if ((deviceStatus === 'up' || deviceStatus === 'online') && 
                 previousAlertsRef.current.has(`${statusKey}_offline`)) {
        // Device came back online
        addNotification({
          title: 'Device Online',
          message: `${deviceName} is back online`,
          priority: 'success',
          category: 'deviceOffline',
          deviceId: device.id,
          deviceName,
          status: deviceStatus
        })
      }

      // Maintenance Mode Alerts - check if enabled
      const maintenanceKey = `${device.id}_maintenance`
      
      if (device.maintenance_mode && alertThresholds.enabled.maintenance) {
        // Always add to current alerts for ongoing maintenance state
        currentAlerts.add(maintenanceKey)
        
        // Only trigger notification on state change (entering maintenance mode)
        if (!previousAlertsRef.current.has(maintenanceKey)) {
          addNotification({
            title: 'Maintenance Mode Enabled',
            message: `${deviceName} has been put into maintenance mode`,
            priority: 'info',
            category: 'deviceOffline',
            deviceId: device.id,
            deviceName
          })
        }
      } else if (!device.maintenance_mode && previousAlertsRef.current.has(maintenanceKey)) {
        // Device exited maintenance mode - optional notification
        addNotification({
          title: 'Maintenance Mode Disabled',
          message: `${deviceName} is no longer in maintenance mode`,
          priority: 'success',
          category: 'deviceOffline',
          deviceId: device.id,
          deviceName
        })
      }
    })

    // Update the previous alerts reference
    previousAlertsRef.current = currentAlerts

  }, [devices, deviceStatuses, alertThresholds, addNotification])

  // Return utility functions for manual alert triggering
  return {
    triggerStockAlert: (deviceId, stockPercentage) => {
      const device = devices.find(d => d.id === deviceId)
      if (!device) return

      const deviceName = device.name || device.id
      let priority, title

      if (stockPercentage === 0) {
        priority = 'critical'
        title = 'Out of Stock Alert'
      } else if (stockPercentage < 20) {
        priority = 'critical'
        title = 'Critical Stock Alert'
      } else {
        priority = 'warning'
        title = 'Low Stock Warning'
      }

      addNotification({
        title,
        message: `${deviceName} stock level: ${stockPercentage}%`,
        priority,
        category: 'stockAlerts',
        deviceId,
        deviceName,
        stockPercentage
      })
    },

    triggerDeviceAlert: (deviceId, status, message) => {
      const device = devices.find(d => d.id === deviceId)
      if (!device) return

      const deviceName = device.name || device.id
      
      addNotification({
        title: `Device ${status.charAt(0).toUpperCase() + status.slice(1)}`,
        message: message || `${deviceName} is ${status}`,
        priority: status === 'offline' ? 'warning' : 'info',
        category: 'deviceOffline',
        deviceId,
        deviceName,
        status
      })
    }
  }
}